/**
 * Document Type Configuration
 * Comprehensive mortgage document type registry with smart filtering
 *
 * Classification-only (Phase 1) - No extraction yet
 */

export type LoanType = 'conventional' | 'fha' | 'va' | 'usda' | 'jumbo';
export type LoanPurpose = 'purchase' | 'refinance' | 'cashout';

export interface DocumentTypeConfig {
  category: string;
  group: string;
  displayName: string;
  shortName: string;
  keywords: string[];
  indicators: string[];
  loanTypes: LoanType[] | ['all'];
  loanPurposes: LoanPurpose[] | ['all'];
  filenameTemplate?: string; // For Phase 2 (extraction)
}

export type LoanDocumentTypeKey = keyof typeof DOCUMENT_TYPES;

/**
 * 300+ Document Type Registry
 * Organized by category from company naming standard
 */
export const DOCUMENT_TYPES = {
  // ============================================================================
  // APPRAISAL (23 types)
  // ============================================================================
  appraisal_1004d: {
    category: 'Appraisal',
    group: 'Appraisal Forms',
    displayName: '1004D Form',
    shortName: '1004D',
    keywords: ['1004D', 'Freddie Mac', 'appraisal update'],
    indicators: ['Form 1004D', 'Appraisal Update', 'Completion Report'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_full: {
    category: 'Appraisal',
    group: 'Appraisal Forms',
    displayName: 'Full Appraisal',
    shortName: 'Appraisal',
    keywords: ['appraisal report', 'uniform residential appraisal', '1004', 'URAR'],
    indicators: ['appraised value', 'subject property', 'comparable sales', 'cost approach'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_1004d_recert: {
    category: 'Appraisal',
    group: 'Appraisal Forms',
    displayName: '1004D Recertification',
    shortName: '1004D-Recert',
    keywords: ['1004D', 'recertification', 'recer'],
    indicators: ['recertification of value', 'Form 1004D'],
    loanTypes: ['all'],
    loanPurposes: ['refinance', 'cashout']
  },

  appraisal_addendum: {
    category: 'Appraisal',
    group: 'Appraisal Forms',
    displayName: 'Appraisal Addendum',
    shortName: 'Appraisal-Add',
    keywords: ['appraisal addendum', 'addendum to appraisal'],
    indicators: ['addendum', 'additional comments', 'supplemental information'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_review: {
    category: 'Appraisal',
    group: 'Appraisal Forms',
    displayName: 'Appraisal Review',
    shortName: 'Appraisal-Review',
    keywords: ['appraisal review', 'desk review', 'field review'],
    indicators: ['review of appraisal', 'appraisal analysis', 'reviewer opinion'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_avm: {
    category: 'Appraisal',
    group: 'Valuations',
    displayName: 'AVM Report',
    shortName: 'AVM',
    keywords: ['AVM', 'automated valuation model', 'electronic valuation'],
    indicators: ['confidence score', 'forecast standard deviation', 'value range'],
    loanTypes: ['all'],
    loanPurposes: ['refinance', 'cashout']
  },

  appraisal_bpo: {
    category: 'Appraisal',
    group: 'Valuations',
    displayName: 'Broker Price Opinion',
    shortName: 'BPO',
    keywords: ['broker price opinion', 'BPO', 'broker opinion of value'],
    indicators: ['broker opinion', 'market analysis', 'comparable properties'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_builders_cert: {
    category: 'Appraisal',
    group: 'Construction',
    displayName: 'Builder Certification',
    shortName: 'Builder-Cert',
    keywords: ['builder certification', 'builder cert', 'certificate of completion'],
    indicators: ['builder certifies', 'construction completed', 'final inspection'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  appraisal_building_permit: {
    category: 'Appraisal',
    group: 'Construction',
    displayName: 'Building Permit',
    shortName: 'Permit',
    keywords: ['building permit', 'construction permit'],
    indicators: ['permit number', 'building department', 'permit issued'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  appraisal_certificate_occupancy: {
    category: 'Appraisal',
    group: 'Construction',
    displayName: 'Certificate of Occupancy',
    shortName: 'COO',
    keywords: ['certificate of occupancy', 'CO', 'occupancy permit'],
    indicators: ['occupancy certificate', 'approved for occupancy', 'building code compliance'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  appraisal_field_review: {
    category: 'Appraisal',
    group: 'Reviews',
    displayName: 'Field Review',
    shortName: 'Field-Review',
    keywords: ['field review', 'property inspection'],
    indicators: ['field review report', 'property reinspection', 'field reviewer'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_nov: {
    category: 'Appraisal',
    group: 'Valuations',
    displayName: 'Notice of Value (NOV)',
    shortName: 'NOV',
    keywords: ['notice of value', 'NOV', 'FHA notice'],
    indicators: ['FHA case number', 'notice of value', 'property value'],
    loanTypes: ['fha'],
    loanPurposes: ['all']
  },

  appraisal_pdr: {
    category: 'Appraisal',
    group: 'Valuations',
    displayName: 'Property Data Report',
    shortName: 'PDR',
    keywords: ['property data report', 'PDR', 'property report'],
    indicators: ['property data', 'public records', 'MLS data'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  appraisal_collateral_underwriter: {
    category: 'Appraisal',
    group: 'Risk Assessment',
    displayName: 'Collateral Underwriter Report',
    shortName: 'CU',
    keywords: ['collateral underwriter', 'CU', 'Fannie Mae CU'],
    indicators: ['risk score', 'CU report', 'appraisal quality'],
    loanTypes: ['conventional'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // ASSETS (13 types)
  // ============================================================================
  asset_bank_statement: {
    category: 'Assets',
    group: 'Bank Accounts',
    displayName: 'Bank Statement',
    shortName: 'BankStmt',
    keywords: ['bank statement', 'checking', 'savings', 'account statement'],
    indicators: ['account number', 'beginning balance', 'ending balance', 'statement period', 'transactions'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  asset_retirement_statement: {
    category: 'Assets',
    group: 'Retirement Accounts',
    displayName: 'Retirement Account Statement',
    shortName: 'Retirement',
    keywords: ['401k', '401(k)', 'IRA', 'retirement account', 'pension'],
    indicators: ['account value', 'vested balance', 'contribution', 'retirement plan'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  asset_gift_letter: {
    category: 'Assets',
    group: 'Gift Funds',
    displayName: 'Gift Letter',
    shortName: 'Gift',
    keywords: ['gift letter', 'gift of funds', 'donor'],
    indicators: ['gift amount', 'donor relationship', 'no repayment', 'gift funds'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  asset_vod: {
    category: 'Assets',
    group: 'Verifications',
    displayName: 'Verification of Deposit',
    shortName: 'VOD',
    keywords: ['verification of deposit', 'VOD', 'deposit verification'],
    indicators: ['account verification', 'current balance', 'average balance'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  asset_stock_certificate: {
    category: 'Assets',
    group: 'Investments',
    displayName: 'Stock Certificate',
    shortName: 'Stock',
    keywords: ['stock certificate', 'shares', 'securities'],
    indicators: ['certificate number', 'shares', 'stock', 'securities'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  asset_life_insurance: {
    category: 'Assets',
    group: 'Insurance',
    displayName: 'Life Insurance Policy',
    shortName: 'Life-Ins',
    keywords: ['life insurance', 'policy', 'cash value'],
    indicators: ['policy number', 'cash surrender value', 'beneficiary'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  asset_proceeds_sale: {
    category: 'Assets',
    group: 'Proceeds',
    displayName: 'Proceeds of Sale',
    shortName: 'Proceeds',
    keywords: ['proceeds of sale', 'settlement statement', 'HUD-1'],
    indicators: ['sale proceeds', 'net proceeds', 'closing statement'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  asset_source_deposit: {
    category: 'Assets',
    group: 'Explanations',
    displayName: 'Source of Deposit',
    shortName: 'Source-Deposit',
    keywords: ['source of deposit', 'large deposit', 'deposit explanation'],
    indicators: ['deposit source', 'explanation', 'documentation'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // AUS (4 types)
  // ============================================================================
  aus_du: {
    category: 'AUS',
    group: 'Automated Underwriting',
    displayName: 'Desktop Underwriter (DU)',
    shortName: 'DU',
    keywords: ['desktop underwriter', 'DU', 'Fannie Mae', 'automated underwriting'],
    indicators: ['DU findings', 'risk class', 'Fannie Mae', 'recommendation'],
    loanTypes: ['conventional'],
    loanPurposes: ['all']
  },

  aus_lp: {
    category: 'AUS',
    group: 'Automated Underwriting',
    displayName: 'Loan Prospector (LP)',
    shortName: 'LP',
    keywords: ['loan prospector', 'LP', 'Freddie Mac', 'automated underwriting'],
    indicators: ['LP findings', 'feedback certificate', 'Freddie Mac', 'approve/eligible'],
    loanTypes: ['conventional'],
    loanPurposes: ['all']
  },

  aus_gus: {
    category: 'AUS',
    group: 'Automated Underwriting',
    displayName: 'GUS (USDA)',
    shortName: 'GUS',
    keywords: ['GUS', 'guaranteed underwriting system', 'USDA', 'rural development'],
    indicators: ['GUS findings', 'USDA', 'rural development', 'accept/refer'],
    loanTypes: ['usda'],
    loanPurposes: ['all']
  },

  aus_total_scorecard: {
    category: 'AUS',
    group: 'Automated Underwriting',
    displayName: 'TOTAL Scorecard (FHA)',
    shortName: 'TOTAL',
    keywords: ['TOTAL scorecard', 'FHA scorecard', 'technology open to approved lenders'],
    indicators: ['TOTAL findings', 'FHA', 'accept/refer', 'scorecard'],
    loanTypes: ['fha'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // CREDIT REPORT (10 types)
  // ============================================================================
  credit_report: {
    category: 'Credit',
    group: 'Credit Reports',
    displayName: 'Credit Report',
    shortName: 'Credit',
    keywords: ['credit report', 'tri-merge', 'credit score', 'FICO'],
    indicators: ['credit score', 'trade lines', 'inquiries', 'public records', 'Experian', 'TransUnion', 'Equifax'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  credit_loe_derogs: {
    category: 'Credit',
    group: 'Explanations',
    displayName: 'LOE - Derogatory Credit',
    shortName: 'LOE-Derogs',
    keywords: ['letter of explanation', 'LOE', 'derogatory', 'late payment', 'collection'],
    indicators: ['letter of explanation', 'credit explanation', 'late payment', 'collection'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  credit_loe_inquiries: {
    category: 'Credit',
    group: 'Explanations',
    displayName: 'LOE - Credit Inquiries',
    shortName: 'LOE-Inquiries',
    keywords: ['letter of explanation', 'LOE', 'inquiries', 'credit pull'],
    indicators: ['letter of explanation', 'inquiry explanation', 'credit inquiries'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  credit_loe_addresses: {
    category: 'Credit',
    group: 'Explanations',
    displayName: 'LOE - Address History',
    shortName: 'LOE-Address',
    keywords: ['letter of explanation', 'LOE', 'address', 'residence history'],
    indicators: ['letter of explanation', 'address history', 'residence explanation'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  credit_bankruptcy_discharge: {
    category: 'Credit',
    group: 'Bankruptcy',
    displayName: 'Bankruptcy Discharge',
    shortName: 'BK-Discharge',
    keywords: ['bankruptcy discharge', 'chapter 7', 'chapter 13', 'discharge papers'],
    indicators: ['discharge of debtor', 'bankruptcy court', 'chapter', 'case number'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  credit_verification_ssn: {
    category: 'Credit',
    group: 'Verifications',
    displayName: 'SSN Verification',
    shortName: 'SSN-Verify',
    keywords: ['social security verification', 'SSN', 'SSA-89'],
    indicators: ['social security', 'SSN', 'verification', 'SSA'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // INCOME (28 types)
  // ============================================================================
  income_w2: {
    category: 'Income',
    group: 'W2 Forms',
    displayName: 'W-2 Form',
    shortName: 'W2',
    keywords: ['W-2', 'wage and tax statement', 'Form W-2'],
    indicators: ['Form W-2', 'Box 1', 'Box 2', 'Employer identification number', 'wages, tips, other compensation'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_paystub: {
    category: 'Income',
    group: 'Pay Stubs',
    displayName: 'Pay Stub',
    shortName: 'Paystub',
    keywords: ['pay stub', 'paystub', 'earnings statement', 'paycheck'],
    indicators: ['pay period', 'gross pay', 'net pay', 'YTD', 'deductions'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_tax_return: {
    category: 'Income',
    group: 'Tax Returns',
    displayName: 'Tax Return',
    shortName: 'TaxReturn',
    keywords: ['tax return', 'Form 1040', 'IRS', '1040'],
    indicators: ['Form 1040', 'U.S. Individual Income Tax Return', 'adjusted gross income', 'filing status'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_1099_misc: {
    category: 'Income',
    group: '1099 Forms',
    displayName: '1099-MISC',
    shortName: '1099-MISC',
    keywords: ['1099-MISC', 'Form 1099', 'miscellaneous income'],
    indicators: ['Form 1099-MISC', 'nonemployee compensation', 'payer', 'recipient'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_1099_int: {
    category: 'Income',
    group: '1099 Forms',
    displayName: '1099-INT',
    shortName: '1099-INT',
    keywords: ['1099-INT', 'interest income'],
    indicators: ['Form 1099-INT', 'interest income', 'payer'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_1099_div: {
    category: 'Income',
    group: '1099 Forms',
    displayName: '1099-DIV',
    shortName: '1099-DIV',
    keywords: ['1099-DIV', 'dividends', 'capital gains'],
    indicators: ['Form 1099-DIV', 'dividends and distributions', 'total ordinary dividends'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_1099_r: {
    category: 'Income',
    group: '1099 Forms',
    displayName: '1099-R',
    shortName: '1099-R',
    keywords: ['1099-R', 'distributions', 'pensions', 'retirement'],
    indicators: ['Form 1099-R', 'distributions from pensions', 'gross distribution'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_voe: {
    category: 'Income',
    group: 'Employment Verification',
    displayName: 'Verification of Employment',
    shortName: 'VOE',
    keywords: ['verification of employment', 'VOE', 'employment verification'],
    indicators: ['employer verification', 'position', 'salary', 'employment dates'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_offer_letter: {
    category: 'Income',
    group: 'Employment Verification',
    displayName: 'Offer Letter',
    shortName: 'Offer',
    keywords: ['offer letter', 'employment offer', 'job offer'],
    indicators: ['offer of employment', 'position', 'salary', 'start date'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_profit_loss: {
    category: 'Income',
    group: 'Self-Employment',
    displayName: 'Profit & Loss Statement',
    shortName: 'PnL',
    keywords: ['profit and loss', 'P&L', 'income statement'],
    indicators: ['profit and loss', 'revenue', 'expenses', 'net income'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_balance_sheet: {
    category: 'Income',
    group: 'Self-Employment',
    displayName: 'Balance Sheet',
    shortName: 'BalanceSheet',
    keywords: ['balance sheet', 'assets', 'liabilities', 'equity'],
    indicators: ['balance sheet', 'total assets', 'total liabilities', 'net worth'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_k1: {
    category: 'Income',
    group: 'Business Income',
    displayName: 'Schedule K-1',
    shortName: 'K1',
    keywords: ['Schedule K-1', 'K-1', 'partnership', 'S corporation'],
    indicators: ['Schedule K-1', 'partner share of income', 'shareholder share'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_social_security: {
    category: 'Income',
    group: 'Benefit Income',
    displayName: 'Social Security Award Letter',
    shortName: 'SSA',
    keywords: ['social security', 'SSA', 'award letter', 'benefit verification'],
    indicators: ['social security administration', 'monthly benefit', 'award amount'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  income_pension: {
    category: 'Income',
    group: 'Benefit Income',
    displayName: 'Pension/Annuity Statement',
    shortName: 'Pension',
    keywords: ['pension', 'annuity', 'retirement income'],
    indicators: ['pension plan', 'monthly payment', 'annuity'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // PROPERTY (17 types)
  // ============================================================================
  property_purchase_agreement: {
    category: 'Property',
    group: 'Purchase Contracts',
    displayName: 'Purchase Agreement',
    shortName: 'Purchase',
    keywords: ['purchase agreement', 'sales contract', 'purchase contract'],
    indicators: ['buyer', 'seller', 'purchase price', 'earnest money', 'closing date'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  property_contract_addendum: {
    category: 'Property',
    group: 'Purchase Contracts',
    displayName: 'Contract Addendum',
    shortName: 'Addendum',
    keywords: ['addendum', 'amendment', 'contract modification'],
    indicators: ['addendum to contract', 'amendment', 'additional terms'],
    loanTypes: ['all'],
    loanPurposes: ['purchase']
  },

  property_flood_cert: {
    category: 'Property',
    group: 'Flood',
    displayName: 'Flood Certification',
    shortName: 'Flood-Cert',
    keywords: ['flood certification', 'flood zone', 'FEMA', 'SFHA'],
    indicators: ['flood zone determination', 'FEMA', 'special flood hazard area', 'community panel number'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_flood_insurance: {
    category: 'Property',
    group: 'Flood',
    displayName: 'Flood Insurance',
    shortName: 'Flood-Ins',
    keywords: ['flood insurance', 'NFIP', 'flood policy'],
    indicators: ['flood insurance policy', 'NFIP', 'coverage amount', 'flood zone'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_insurance_declarations: {
    category: 'Property',
    group: 'Hazard Insurance',
    displayName: 'Insurance Declarations',
    shortName: 'Ins-Dec',
    keywords: ['insurance declarations', 'homeowners insurance', 'hazard insurance', 'declarations page'],
    indicators: ['declarations page', 'coverage amount', 'policy number', 'effective date', 'premium'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_insurance_quote: {
    category: 'Property',
    group: 'Hazard Insurance',
    displayName: 'Insurance Quote',
    shortName: 'Ins-Quote',
    keywords: ['insurance quote', 'insurance estimate', 'premium quote'],
    indicators: ['insurance quote', 'estimated premium', 'coverage estimate'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_hoa_statement: {
    category: 'Property',
    group: 'HOA/Condo',
    displayName: 'HOA Statement',
    shortName: 'HOA-Stmt',
    keywords: ['HOA', 'homeowners association', 'HOA statement', 'condo fee'],
    indicators: ['homeowners association', 'monthly dues', 'HOA fee', 'assessment'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_hoa_cert: {
    category: 'Property',
    group: 'HOA/Condo',
    displayName: 'HOA Certification',
    shortName: 'HOA-Cert',
    keywords: ['HOA certification', 'questionnaire', 'condo cert'],
    indicators: ['homeowners association certification', 'HOA questionnaire', 'budget', 'reserves'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_hoa_budget: {
    category: 'Property',
    group: 'HOA/Condo',
    displayName: 'HOA Budget',
    shortName: 'HOA-Budget',
    keywords: ['HOA budget', 'association budget', 'operating budget'],
    indicators: ['homeowners association budget', 'annual budget', 'operating expenses', 'reserve fund'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_lease_agreement: {
    category: 'Property',
    group: 'Rental Income',
    displayName: 'Lease Agreement',
    shortName: 'Lease',
    keywords: ['lease agreement', 'rental agreement', 'tenant lease'],
    indicators: ['lease agreement', 'landlord', 'tenant', 'monthly rent', 'lease term'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  property_termite_report: {
    category: 'Property',
    group: 'Inspections',
    displayName: 'Termite Report',
    shortName: 'Termite',
    keywords: ['termite report', 'pest inspection', 'wood destroying insect'],
    indicators: ['termite inspection', 'pest control', 'wood destroying organisms', 'clear report'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // TITLE (20 types)
  // ============================================================================
  title_commitment: {
    category: 'Title',
    group: 'Title Insurance',
    displayName: 'Title Commitment',
    shortName: 'Title-Commit',
    keywords: ['title commitment', 'preliminary title report', 'title insurance commitment'],
    indicators: ['schedule A', 'schedule B', 'exceptions', 'requirements', 'title commitment'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_policy: {
    category: 'Title',
    group: 'Title Insurance',
    displayName: 'Title Insurance Policy',
    shortName: 'Title-Policy',
    keywords: ['title insurance policy', 'owner policy', 'lender policy'],
    indicators: ['title insurance policy', 'policy amount', 'insured', 'effective date'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_payoff_statement: {
    category: 'Title',
    group: 'Payoffs',
    displayName: 'Payoff Statement',
    shortName: 'Payoff',
    keywords: ['payoff statement', 'mortgage payoff', 'loan payoff'],
    indicators: ['payoff amount', 'good through date', 'per diem', 'principal balance'],
    loanTypes: ['all'],
    loanPurposes: ['refinance', 'cashout']
  },

  title_subordination: {
    category: 'Title',
    group: 'Liens',
    displayName: 'Subordination Agreement',
    shortName: 'Subordination',
    keywords: ['subordination agreement', 'HELOC subordination', 'second lien'],
    indicators: ['subordination agreement', 'first lien position', 'second lien position'],
    loanTypes: ['all'],
    loanPurposes: ['refinance', 'cashout']
  },

  title_survey: {
    category: 'Title',
    group: 'Surveys',
    displayName: 'Survey',
    shortName: 'Survey',
    keywords: ['survey', 'property survey', 'boundary survey', 'plat'],
    indicators: ['surveyor', 'property boundaries', 'legal description', 'survey date'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_deed: {
    category: 'Title',
    group: 'Deeds',
    displayName: 'Deed',
    shortName: 'Deed',
    keywords: ['deed', 'grant deed', 'warranty deed', 'quitclaim deed'],
    indicators: ['grantor', 'grantee', 'legal description', 'recording information'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_trust_agreement: {
    category: 'Title',
    group: 'Trusts',
    displayName: 'Trust Agreement',
    shortName: 'Trust',
    keywords: ['trust agreement', 'living trust', 'revocable trust'],
    indicators: ['trust agreement', 'trustee', 'grantor', 'trust name'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_trust_cert: {
    category: 'Title',
    group: 'Trusts',
    displayName: 'Trust Certification',
    shortName: 'Trust-Cert',
    keywords: ['trust certification', 'certification of trust', 'trust excerpt'],
    indicators: ['certification of trust', 'trustee powers', 'trust date'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  title_poa: {
    category: 'Title',
    group: 'Legal',
    displayName: 'Power of Attorney',
    shortName: 'POA',
    keywords: ['power of attorney', 'POA', 'attorney in fact'],
    indicators: ['power of attorney', 'attorney-in-fact', 'principal', 'agent'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // DISCLOSURES (25 types)
  // ============================================================================
  disclosures_loan_estimate: {
    category: 'Disclosures',
    group: 'TRID',
    displayName: 'Loan Estimate',
    shortName: 'LE',
    keywords: ['loan estimate', 'LE', 'TRID'],
    indicators: ['loan estimate', 'loan terms', 'projected payments', 'costs at closing'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_closing_disclosure: {
    category: 'Disclosures',
    group: 'TRID',
    displayName: 'Closing Disclosure',
    shortName: 'CD',
    keywords: ['closing disclosure', 'CD', 'TRID'],
    indicators: ['closing disclosure', 'loan costs', 'other costs', 'cash to close'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_initial_1003: {
    category: 'Disclosures',
    group: 'Application',
    displayName: 'Initial 1003',
    shortName: 'Initial-1003',
    keywords: ['loan application', '1003', 'URLA', 'uniform residential loan application'],
    indicators: ['uniform residential loan application', 'borrower information', 'financial information'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_final_1003: {
    category: 'Disclosures',
    group: 'Application',
    displayName: 'Final 1003',
    shortName: 'Final-1003',
    keywords: ['final loan application', 'final 1003', 'URLA'],
    indicators: ['uniform residential loan application', 'borrower signature', 'final application'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_4506c: {
    category: 'Disclosures',
    group: 'IRS Forms',
    displayName: 'IRS Form 4506-C',
    shortName: '4506C',
    keywords: ['4506-C', '4506C', 'IRS transcript', 'tax transcript authorization'],
    indicators: ['Form 4506-C', 'request for transcript', 'IRS', 'tax return information'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_econsent: {
    category: 'Disclosures',
    group: 'Electronic',
    displayName: 'E-Consent',
    shortName: 'E-Consent',
    keywords: ['electronic consent', 'e-consent', 'electronic signature'],
    indicators: ['electronic consent', 'e-sign agreement', 'electronic delivery'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  disclosures_credit_auth: {
    category: 'Disclosures',
    group: 'Authorizations',
    displayName: 'Credit Authorization',
    shortName: 'Credit-Auth',
    keywords: ['credit authorization', 'consent to pull credit', 'credit check authorization'],
    indicators: ['authorization to obtain credit', 'credit report authorization', 'credit inquiry consent'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // VA DOCUMENTS (16 types) - VA loans only
  // ============================================================================
  va_dd214: {
    category: 'VA',
    group: 'Service Records',
    displayName: 'DD Form 214',
    shortName: 'DD214',
    keywords: ['DD214', 'DD Form 214', 'certificate of release', 'discharge papers'],
    indicators: ['certificate of release or discharge from active duty', 'DD FORM 214', 'character of service', 'branch of service'],
    loanTypes: ['va'],
    loanPurposes: ['all']
  },

  va_coe: {
    category: 'VA',
    group: 'Eligibility',
    displayName: 'Certificate of Eligibility',
    shortName: 'COE',
    keywords: ['certificate of eligibility', 'COE', 'VA eligibility', 'entitlement'],
    indicators: ['certificate of eligibility', 'VA home loan', 'entitlement', 'service member'],
    loanTypes: ['va'],
    loanPurposes: ['all']
  },

  va_funding_fee: {
    category: 'VA',
    group: 'Fees',
    displayName: 'VA Funding Fee Notice',
    shortName: 'Funding-Fee',
    keywords: ['VA funding fee', 'funding fee notice'],
    indicators: ['VA funding fee', 'funding fee exemption', 'service-connected disability'],
    loanTypes: ['va'],
    loanPurposes: ['all']
  },

  va_loan_summary: {
    category: 'VA',
    group: 'Underwriting',
    displayName: 'VA Loan Summary Sheet',
    shortName: 'VA-Summary',
    keywords: ['VA loan summary', 'loan summary sheet', 'VA underwriting'],
    indicators: ['VA loan summary', 'residual income', 'DTI ratio', 'VA requirements'],
    loanTypes: ['va'],
    loanPurposes: ['all']
  },

  va_irrrl: {
    category: 'VA',
    group: 'Refinance',
    displayName: 'VA IRRRL Worksheet',
    shortName: 'IRRRL',
    keywords: ['IRRRL', 'interest rate reduction', 'streamline refinance', 'VA refinance'],
    indicators: ['interest rate reduction refinance loan', 'IRRRL', 'net tangible benefit'],
    loanTypes: ['va'],
    loanPurposes: ['refinance']
  },

  // ============================================================================
  // FHA DOCUMENTS (12 types) - FHA loans only
  // ============================================================================
  fha_case_number: {
    category: 'FHA',
    group: 'Case Assignment',
    displayName: 'FHA Case Number Assignment',
    shortName: 'FHA-Case',
    keywords: ['FHA case number', 'case assignment', 'HUD case number'],
    indicators: ['FHA case number', 'case assignment', 'HUD', 'FHA connection'],
    loanTypes: ['fha'],
    loanPurposes: ['all']
  },

  fha_conditional_commitment: {
    category: 'FHA',
    group: 'Underwriting',
    displayName: 'FHA Conditional Commitment',
    shortName: 'FHA-Conditional',
    keywords: ['FHA conditional commitment', 'conditional approval', 'DE underwriting'],
    indicators: ['conditional commitment', 'FHA approval', 'conditions', 'direct endorsement'],
    loanTypes: ['fha'],
    loanPurposes: ['all']
  },

  fha_amendatory_clause: {
    category: 'FHA',
    group: 'Disclosures',
    displayName: 'FHA Amendatory Clause',
    shortName: 'Amendatory',
    keywords: ['FHA amendatory clause', 'real estate certification'],
    indicators: ['FHA amendatory clause', 'appraised value', 'purchase price', 'real estate certification'],
    loanTypes: ['fha'],
    loanPurposes: ['purchase']
  },

  fha_203k_consultant: {
    category: 'FHA',
    group: '203(k) Rehabilitation',
    displayName: '203(k) Consultant Report',
    shortName: '203k-Report',
    keywords: ['203k', '203(k)', 'consultant report', 'rehabilitation'],
    indicators: ['203(k) consultant', 'rehabilitation plan', 'work write-up', 'cost estimate'],
    loanTypes: ['fha'],
    loanPurposes: ['purchase', 'refinance']
  },

  // ============================================================================
  // USDA DOCUMENTS (4 types) - USDA loans only
  // ============================================================================
  usda_eligibility: {
    category: 'USDA',
    group: 'Eligibility',
    displayName: 'USDA Eligibility Determination',
    shortName: 'USDA-Eligible',
    keywords: ['USDA eligibility', 'rural development', 'property eligibility'],
    indicators: ['USDA eligibility', 'rural development', 'eligible area', 'property eligibility'],
    loanTypes: ['usda'],
    loanPurposes: ['all']
  },

  usda_conditional_commitment: {
    category: 'USDA',
    group: 'Underwriting',
    displayName: 'USDA Conditional Commitment',
    shortName: 'USDA-Conditional',
    keywords: ['USDA conditional commitment', 'rural development approval'],
    indicators: ['conditional commitment', 'USDA', 'rural development', 'approved with conditions'],
    loanTypes: ['usda'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // ENTITY DOCUMENTS (8 types)
  // ============================================================================
  entity_articles_incorporation: {
    category: 'Entity',
    group: 'Formation',
    displayName: 'Articles of Incorporation',
    shortName: 'Articles-Inc',
    keywords: ['articles of incorporation', 'corporate charter', 'certificate of incorporation'],
    indicators: ['articles of incorporation', 'state of incorporation', 'corporation name'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  entity_operating_agreement: {
    category: 'Entity',
    group: 'Formation',
    displayName: 'Operating Agreement',
    shortName: 'Operating-Agmt',
    keywords: ['operating agreement', 'LLC agreement', 'member agreement'],
    indicators: ['operating agreement', 'LLC', 'members', 'ownership percentage'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  entity_ein: {
    category: 'Entity',
    group: 'Tax Documents',
    displayName: 'EIN Letter',
    shortName: 'EIN',
    keywords: ['EIN', 'employer identification number', 'tax ID'],
    indicators: ['employer identification number', 'EIN', 'federal tax identification'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // ID DOCUMENTS (4 types)
  // ============================================================================
  id_drivers_license: {
    category: 'ID',
    group: 'Identification',
    displayName: 'Driver License',
    shortName: 'DL',
    keywords: ['driver license', 'drivers license', 'DL', 'state ID'],
    indicators: ['driver license', 'license number', 'expiration date', 'date of birth'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  id_passport: {
    category: 'ID',
    group: 'Identification',
    displayName: 'Passport',
    shortName: 'Passport',
    keywords: ['passport', 'U.S. passport'],
    indicators: ['passport', 'passport number', 'country code', 'expiration date'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  id_social_security_card: {
    category: 'ID',
    group: 'Identification',
    displayName: 'Social Security Card',
    shortName: 'SS-Card',
    keywords: ['social security card', 'SSN card'],
    indicators: ['social security', 'social security number', 'SSA'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  // ============================================================================
  // MISCELLANEOUS (10 types)
  // ============================================================================
  misc_loe: {
    category: 'Miscellaneous',
    group: 'Explanations',
    displayName: 'Letter of Explanation',
    shortName: 'LOE',
    keywords: ['letter of explanation', 'LOE', 'explanation letter'],
    indicators: ['letter of explanation', 'regarding', 'explanation'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  misc_invoice: {
    category: 'Miscellaneous',
    group: 'Invoices',
    displayName: 'Invoice',
    shortName: 'Invoice',
    keywords: ['invoice', 'bill', 'receipt'],
    indicators: ['invoice', 'amount due', 'payment', 'total'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  misc_settlement_statement: {
    category: 'Miscellaneous',
    group: 'Closing',
    displayName: 'Settlement Statement',
    shortName: 'Settlement',
    keywords: ['settlement statement', 'HUD-1', 'closing statement'],
    indicators: ['settlement statement', 'borrower', 'seller', 'settlement charges'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  misc_broker_check: {
    category: 'Miscellaneous',
    group: 'Payments',
    displayName: 'Broker Check',
    shortName: 'Broker-Check',
    keywords: ['broker payment', 'commission check', 'broker commission', 'payment to broker'],
    indicators: ['check image', 'MICR line', 'routing number', 'account number', 'payee', 'dollar amount', 'check number'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  other: {
    category: 'Other',
    group: 'Uncategorized',
    displayName: 'Other Document',
    shortName: 'Other',
    keywords: ['document', 'file', 'attachment'],
    indicators: [],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  investor_lock: {
    category: 'Pricing',
    group: 'Rate Lock',
    displayName: 'Investor Lock Confirmation',
    shortName: 'Investor Lock',
    keywords: ['investor lock', 'rate lock', 'lock confirmation', 'commitment', 'pricing confirmation'],
    indicators: ['lock date', 'expiration date', 'rate', 'price', 'investor', 'commitment number'],
    loanTypes: ['all'],
    loanPurposes: ['all']
  },

  unknown: {
    category: 'Unknown',
    group: 'Unclassified',
    displayName: 'Unknown Document Type',
    shortName: 'Unknown',
    keywords: [],
    indicators: [],
    loanTypes: ['all'],
    loanPurposes: ['all']
  }
} as const;

/**
 * Filter document types based on loan context
 * Smart filtering reduces classification scope from 300+ to ~100-150 relevant types
 */
export function getApplicableDocumentTypes(
  loanType?: LoanType,
  loanPurpose?: LoanPurpose
): LoanDocumentTypeKey[] {
  return (Object.entries(DOCUMENT_TYPES) as [LoanDocumentTypeKey, DocumentTypeConfig][])
    .filter(([_key, config]) => {
      // Filter by loan type
      if (loanType && !config.loanTypes.includes('all')) {
        if (!config.loanTypes.includes(loanType)) {
          return false;
        }
      }

      // Filter by loan purpose
      if (loanPurpose && !config.loanPurposes.includes('all')) {
        if (!config.loanPurposes.includes(loanPurpose)) {
          return false;
        }
      }

      return true;
    })
    .map(([key]) => key);
}

/**
 * Generate contextual classification prompt
 * Only includes document types applicable to the loan context
 */
export function generateClassificationPrompt(
  loanType?: LoanType,
  loanPurpose?: LoanPurpose
): string {
  const applicableTypes = getApplicableDocumentTypes(loanType, loanPurpose);

  // Group by category for better organization
  const grouped = applicableTypes.reduce((acc, typeKey) => {
    const config = DOCUMENT_TYPES[typeKey];
    if (!acc[config.category]) {
      acc[config.category] = [];
    }
    acc[config.category].push({ key: typeKey, ...config });
    return acc;
  }, {} as Record<string, Array<{ key: LoanDocumentTypeKey } & DocumentTypeConfig>>);

  let prompt = '';

  // Add loan context header
  if (loanType || loanPurpose) {
    prompt += `LOAN CONTEXT:\n`;
    if (loanType) {
prompt += `- Loan Type: ${loanType.toUpperCase()}\n`;
}
    if (loanPurpose) {
prompt += `- Loan Purpose: ${loanPurpose}\n`;
}
    prompt += '\n';
  }

  prompt += `APPLICABLE DOCUMENT TYPES (${applicableTypes.length} types):\n\n`;

  // Generate type list grouped by category
  Object.entries(grouped).forEach(([category, types]) => {
    prompt += `**${category.toUpperCase()}:**\n`;
    types.forEach((type, idx) => {
      prompt += `${idx + 1}. **${type.key}** - ${type.displayName}\n`;
      if (type.keywords.length > 0) {
        prompt += `   Keywords: ${type.keywords.slice(0, 5).join(', ')}\n`;
      }
      if (type.indicators.length > 0) {
        prompt += `   Indicators: ${type.indicators.slice(0, 3).join(', ')}\n`;
      }
      prompt += '\n';
    });
  });

  return prompt;
}

/**
 * Get display name for a document type
 */
export function getDocumentTypeLabel(typeKey: LoanDocumentTypeKey): string {
  return DOCUMENT_TYPES[typeKey]?.displayName || 'Unknown';
}

/**
 * Get short name for filename generation
 */
export function getDocumentTypeShortName(typeKey: LoanDocumentTypeKey): string {
  return DOCUMENT_TYPES[typeKey]?.shortName || 'Doc';
}
